import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, ClipboardCheck, AlertCircle } from "lucide-react";

/**
 * ReadinessSignoffDialog — modal that captures a manager's formal review
 * and locks the current readiness assessment as a historical record.
 *
 * Deliberately dumb about business rules: it only calls
 * POST /dashboard/readiness/signoffs. Freeze / permission / audit is
 * handled server-side.
 */
export default function ReadinessSignoffDialog({ open, onOpenChange, readiness, user, onSigned }) {
  const [comment, setComment] = useState("");
  const [role, setRole] = useState("");
  const [roleOptions, setRoleOptions] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setComment(""); setConfirmed(false);
    api.get("/dashboard/readiness/signoffs/role-options")
      .then((r) => {
        const opts = Array.isArray(r.data?.options) ? r.data.options : [];
        setRoleOptions(opts);
        setRole(r.data?.default || opts[0] || "Manager");
      })
      .catch(() => { setRoleOptions([]); setRole("Manager"); });
  }, [open]);

  const submit = async () => {
    if (!confirmed) return;
    setSaving(true);
    try {
      const r = await api.post("/dashboard/readiness/signoffs", {
        confirm: true,
        role,
        comment: comment.trim() || null,
      });
      toast.success("Readiness assessment signed");
      onSigned && onSigned(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const assessmentDate = readiness?.generated_at
    ? new Date(readiness.generated_at).toLocaleString(undefined, {
        day: "numeric", month: "long", year: "numeric",
      })
    : "—";
  const summary = readiness || {};
  const priorityActionsCount = (summary.recommendations || []).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-lg" data-testid="readiness-signoff-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            Sign CQC Inspection Readiness Assessment
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-600">
            You are confirming that you have reviewed AYOBELL's current inspection-readiness
            assessment, including identified strengths, risks, evidence gaps and priority actions.
          </DialogDescription>
        </DialogHeader>

        <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60 text-[12px] space-y-1"
             data-testid="signoff-summary">
          <div className="flex justify-between"><span className="text-slate-500">Readiness Score</span>
            <span className="font-semibold" data-testid="signoff-summary-score">{summary.score ?? "—"} / 100</span>
          </div>
          <div className="flex justify-between"><span className="text-slate-500">Readiness Status</span>
            <span className="font-semibold" data-testid="signoff-summary-band">{summary.band_label || "—"}</span>
          </div>
          <div className="flex justify-between"><span className="text-slate-500">Priority Actions</span>
            <span className="font-semibold" data-testid="signoff-summary-actions">{priorityActionsCount}</span>
          </div>
          <div className="flex justify-between"><span className="text-slate-500">Areas Performing Strongly</span>
            <span className="font-semibold">{summary.strong_count ?? 0}</span>
          </div>
          <div className="flex justify-between"><span className="text-slate-500">Areas Requiring Attention</span>
            <span className="font-semibold">{summary.attention_count ?? 0}</span>
          </div>
          <div className="flex justify-between"><span className="text-slate-500">Assessment Date</span>
            <span className="font-semibold">{assessmentDate}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Reviewed by</Label>
            <Input
              value={user?.name || user?.email || ""}
              readOnly
              className="h-11 md:h-9 mt-1 bg-slate-50"
              data-testid="signoff-reviewer"
            />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1 h-11 md:h-9" data-testid="signoff-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
                {!roleOptions.includes(role) && role && (
                  <SelectItem value={role}>{role}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Optional management comment</Label>
          <Textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any management observations, decisions or follow-up requirements..."
            className="mt-1 text-sm"
            data-testid="signoff-comment"
          />
        </div>

        <label className="flex items-start gap-2 text-xs cursor-pointer bg-emerald-50/60 border border-emerald-200 rounded p-2"
               data-testid="signoff-confirm-wrap">
          <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="signoff-confirm" />
          <span>I confirm that I have reviewed this readiness assessment and the associated priority actions.</span>
        </label>

        <div
          className="bg-sky-50 border border-sky-200 rounded-md p-3 text-[12px] text-sky-900 leading-snug flex items-start gap-2"
          data-testid="signoff-disclaimer"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-sky-700" />
          <div>
            <div className="font-semibold mb-1">About this assessment</div>
            <div>
              This is an AYOBELL internal inspection-readiness assessment. It is not an official CQC rating,
              inspection outcome, approval or endorsement.
            </div>
            <div className="mt-2">
              AYOBELL CQC Inspection Readiness is an internal governance and inspection-preparedness assessment.
              It does not constitute a CQC rating, inspection outcome, regulatory judgement, approval or endorsement.
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="signoff-cancel"
            className="h-11 md:h-9 w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            disabled={!confirmed || saving}
            onClick={submit}
            className="h-11 md:h-9 w-full sm:w-auto bg-emerald-700 hover:bg-emerald-800"
            data-testid="signoff-submit"
          >
            <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Signing…" : "Sign Assessment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
